import React from 'react';
import {FieldArray, FieldArrayRenderProps, useFormikContext} from "formik";
import Button from "components/Button";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {DateTime} from "luxon";
import {TimeRangeField} from "../Field";
import {DateRangePicker, ToggleSwitch} from "../Control";
import {newRange} from "./StandardHoursFieldSet";
import {Exception, Exceptions} from "../../../../types/OpeningHoursNew";

// Types ----

type ExceptionHoursFieldSetProps = {
  name: string
}

// Helpers ----

function parseDate(date: string): DateTime {
  const today = DateTime.now().startOf('day');

  if(date.split('-', 3).length < 3) {
    date = today.year + '-' + date;
  }

  return DateTime.fromISO(date);
}

function exceptionKey(startDate: DateTime, endDate: DateTime): string {
  const relativeFormat = "MM-dd";

  const relativeStartDate = startDate.toFormat(relativeFormat);
  const relativeEndDate = endDate.toFormat(relativeFormat);

  return `${relativeStartDate} to ${relativeEndDate}`;
}

// Component ----

function ExceptionHoursFieldSet({name}: ExceptionHoursFieldSetProps) {
  const form = useFormikContext();

  const exceptionList = (form.values as any)[name].exceptions as Exceptions;

  function addException() {
    const newExceptions: Exceptions = [...(form.values as any)[name].exceptions];

    let nextDate = DateTime.now().startOf('day');

    const dateRanges = newExceptions.map(exception => exception.dateRange);
    const lastDateRange = dateRanges[dateRanges.length - 1];

    if (lastDateRange) {
      const [, endDateString] = lastDateRange.split(' to ', 2);
      nextDate = endDateString ? parseDate(endDateString).plus({day: 1}) : nextDate.plus({day: 1});
    } else {
      nextDate = nextDate.plus({day: 1});
    }

    newExceptions.push({id: crypto.randomUUID(), dateRange: exceptionKey(nextDate, nextDate), timeRanges: []});

    void form.setFieldValue(`${name}.exceptions`, newExceptions);
  }

  function removeException(key: string) {
    const newExceptions = [...(form.values as any)[name].exceptions]
      .filter(exception => exception.dateRange !== key);

    void form.setFieldValue(`${name}.exceptions`, newExceptions)
  }

  function toggleDateRange(dateRange: string, isOpen: boolean) {
    const index = exceptionList.findIndex(exception => exception.dateRange === dateRange);

    void form.setFieldValue(`${name}.exceptions.${index}.timeRanges`, isOpen ? [newRange] : [])
  }

  function changeDate(oldDate: string, newDate: string) {
    void form.setFieldValue(
      `${name}.exceptions`,
      exceptionList.map(({id, dateRange, timeRanges}) => ({ id, dateRange: dateRange === oldDate ? newDate : dateRange, timeRanges }))
    );
  }

  const currentYear = DateTime.now().year;

  return (
    <fieldset>
      <div className="mb-4">
        <legend className="font-medium">Exceptions</legend>
        <p className="text-sm text-gray-500">Override the times on specific days. These repeat annually.</p>
      </div>

      <ul>
        {exceptionList.map(({id, dateRange, timeRanges}, index) => (
          <li key={id} className="mb-6">

            <div className="flex items-center space-x-4 mb-1">
              <ToggleSwitch
                checked={timeRanges.length > 0}
                onChange={enabled => toggleDateRange(dateRange, enabled)}
                short
              />

              <DateRangePicker
                name={`${name}.exceptions.${dateRange}`}
                value={dateRange}
                onChange={range => changeDate(dateRange, range.expression)}
                startMin={`${currentYear}-01-01`}
                startMax={`${currentYear}-12-31`}
                endMax={`${currentYear}-12-31`}
              />

              <Button
                icon={TrashIcon}
                size={Button.sizes.TINY}
                color={Button.colors.WHITE}
                onClick={() => removeException(dateRange)}
              />
            </div>

            {timeRanges.length < 1 &&
              <div className="ml-14 text-sm">Closed</div>
            }

            {timeRanges.length > 0 &&
              <div className="ml-14 mb-2 p-2 bg-gray-100 rounded-md">
                <FieldArray name={`${name}.exceptions.${index}`}>
                  {({push, remove, form}: FieldArrayRenderProps) => {
                    const ranges = timeRanges;
                    const index = form.values[name].exceptions.findIndex((exception: Exception) => exception.dateRange === dateRange);

                    return (
                      <>
                        <ul>
                          {ranges.map((timeRanges, rangeIndex) => (
                            <li key={rangeIndex} className="flex items-center space-x-4 mb-1">
                              <TimeRangeField name={`${name}.exceptions.${index}.timeRanges.${rangeIndex}`}/>

                              {ranges.length > 1 &&
                                <Button
                                  icon={TrashIcon}
                                  size={Button.sizes.TINY}
                                  color={Button.colors.WHITE}
                                  onClick={() => remove(rangeIndex)}
                                />
                              }
                            </li>
                          ))}
                        </ul>

                        {/* TODO: Re-enable this when the audience app supports it */}
                        {/*<div className="mt-1">*/}
                        {/*  <Button*/}
                        {/*    icon={PlusIcon}*/}
                        {/*    size={Button.sizes.TINY}*/}
                        {/*    color={Button.colors.SECONDARY}*/}
                        {/*    onClick={() => push(newRange)}*/}
                        {/*  >*/}
                        {/*    Add Times*/}
                        {/*  </Button>*/}
                        {/*</div>*/}
                      </>
                    )
                  }}
                </FieldArray>
              </div>
            }
          </li>
        ))}
      </ul>

      <div className="mt-1">
        <Button
          icon={PlusIcon}
          size={Button.sizes.TINY}
          color={Button.colors.SECONDARY}
          onClick={addException}
        >
          Add Date
        </Button>
      </div>
    </fieldset>
  );
}

// Export ----

export default ExceptionHoursFieldSet;